export enum FishingDayPhase {
  Night = 'night',
  Dawn = 'dawn',
  Morning = 'morning',
  Day = 'day',
  LateAfternoon = 'late-afternoon',
  Sunset = 'sunset',
}

export const FISHING_DAY_PHASE_LABELS: Record<FishingDayPhase, string> = {
  [FishingDayPhase.Night]: 'Ночь',
  [FishingDayPhase.Dawn]: 'Рассвет',
  [FishingDayPhase.Morning]: 'Утро',
  [FishingDayPhase.Day]: 'День',
  [FishingDayPhase.LateAfternoon]: 'Поздний день',
  [FishingDayPhase.Sunset]: 'Закат',
}

export interface FishingSolarLocation {
  latitude: number
  longitude: number
}

export enum FishingNaturePeriod {
  Daylight = 'daylight',
  AfterSunset = 'after-sunset',
}

export interface FishingSolarTransition {
  at: string
  dayPhase: FishingDayPhase
  naturePeriod: FishingNaturePeriod
}

export interface FishingSolarSchedule {
  generatedAt: string
  validUntil: string
  initialDayPhase: FishingDayPhase
  initialNaturePeriod: FishingNaturePeriod
  transitions: FishingSolarTransition[]
}

const RAD = Math.PI / 180
const JULIAN_UNIX_EPOCH = 2_440_587.5
const JULIAN_J2000 = 2_451_545

function solarAltitudeAndHourAngle(at: Date, location: FishingSolarLocation) {
  const days = (at.getTime() / 86_400_000) + JULIAN_UNIX_EPOCH - JULIAN_J2000
  const meanAnomaly = RAD * (357.5291 + 0.98560028 * days)
  const equationOfCenter = RAD * (
    1.9148 * Math.sin(meanAnomaly)
    + 0.02 * Math.sin(2 * meanAnomaly)
    + 0.0003 * Math.sin(3 * meanAnomaly)
  )
  const eclipticLongitude = meanAnomaly + equationOfCenter + RAD * 102.9372 + Math.PI
  const obliquity = RAD * 23.4397
  const declination = Math.asin(Math.sin(obliquity) * Math.sin(eclipticLongitude))
  const rightAscension = Math.atan2(
    Math.sin(eclipticLongitude) * Math.cos(obliquity),
    Math.cos(eclipticLongitude),
  )
  const siderealTime = RAD * (280.16 + 360.9856235 * days) + location.longitude * RAD
  const hourAngle = Math.atan2(
    Math.sin(siderealTime - rightAscension),
    Math.cos(siderealTime - rightAscension),
  )
  const latitude = location.latitude * RAD
  const altitude = Math.asin(
    Math.sin(latitude) * Math.sin(declination)
    + Math.cos(latitude) * Math.cos(declination) * Math.cos(hourAngle),
  ) / RAD
  return { altitude, hourAngle }
}

/** Uses solar elevation, so seasonal sunrise and sunset changes are automatic. */
export function fishingDayPhaseAtSolarPosition(
  at: Date,
  location: FishingSolarLocation,
): FishingDayPhase {
  const { altitude, hourAngle } = solarAltitudeAndHourAngle(at, location)
  if (altitude < -6) return FishingDayPhase.Night
  if (hourAngle < 0) {
    if (altitude < 6) return FishingDayPhase.Dawn
    if (altitude < 25) return FishingDayPhase.Morning
    return FishingDayPhase.Day
  }
  if (altitude < 6) return FishingDayPhase.Sunset
  if (altitude < 25) return FishingDayPhase.LateAfternoon
  return FishingDayPhase.Day
}

export function fishingNaturePeriodAtSolarPosition(
  at: Date,
  location: FishingSolarLocation,
): FishingNaturePeriod {
  return solarAltitudeAndHourAngle(at, location).altitude < -0.833
    ? FishingNaturePeriod.AfterSunset
    : FishingNaturePeriod.Daylight
}

export function buildFishingSolarSchedule(
  generatedAt: Date,
  location: FishingSolarLocation,
  durationHours = 36,
): FishingSolarSchedule {
  const stepMs = 5 * 60_000
  const validUntil = new Date(generatedAt.getTime() + durationHours * 3_600_000)
  const initialDayPhase = fishingDayPhaseAtSolarPosition(generatedAt, location)
  const initialNaturePeriod = fishingNaturePeriodAtSolarPosition(generatedAt, location)
  const transitions: FishingSolarTransition[] = []
  let previousDayPhase = initialDayPhase
  let previousNaturePeriod = initialNaturePeriod

  for (let timestamp = generatedAt.getTime() + stepMs; timestamp <= validUntil.getTime(); timestamp += stepMs) {
    const at = new Date(timestamp)
    const dayPhase = fishingDayPhaseAtSolarPosition(at, location)
    const naturePeriod = fishingNaturePeriodAtSolarPosition(at, location)
    if (dayPhase === previousDayPhase && naturePeriod === previousNaturePeriod) continue
    transitions.push({ at: at.toISOString(), dayPhase, naturePeriod })
    previousDayPhase = dayPhase
    previousNaturePeriod = naturePeriod
  }

  return {
    generatedAt: generatedAt.toISOString(),
    validUntil: validUntil.toISOString(),
    initialDayPhase,
    initialNaturePeriod,
    transitions,
  }
}

export function fishingSolarStateFromSchedule(schedule: FishingSolarSchedule, at: Date) {
  let dayPhase = schedule.initialDayPhase
  let naturePeriod = schedule.initialNaturePeriod
  for (const transition of schedule.transitions) {
    if (Date.parse(transition.at) > at.getTime()) break
    dayPhase = transition.dayPhase
    naturePeriod = transition.naturePeriod
  }
  return { dayPhase, naturePeriod }
}

export function fishingDayPhaseAtHour(hour: number): FishingDayPhase {
  const normalized = Math.max(0, Math.min(23, Math.trunc(hour)))
  if (normalized >= 22 || normalized < 4) return FishingDayPhase.Night
  if (normalized < 8) return FishingDayPhase.Dawn
  if (normalized < 12) return FishingDayPhase.Morning
  if (normalized < 16) return FishingDayPhase.Day
  if (normalized < 19) return FishingDayPhase.LateAfternoon
  return FishingDayPhase.Sunset
}

export function hourInRiga(now = new Date()): number {
  const hour = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Riga',
    hour: '2-digit',
    hourCycle: 'h23',
  }).format(now)
  const parsed = Number(hour)
  return Number.isInteger(parsed) ? parsed : 12
}
